import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../services/api';
import { useAuth } from './AuthContext';

export interface UserNotification {
    id: string;
    user_id: string;
    type: 'info' | 'warning' | 'success' | 'sos' | 'system';
    title: string;
    message: string;
    link?: string;
    is_read: boolean;
    created_at: string;
}

interface UserNotificationsContextType {
    notifications: UserNotification[];
    unreadCount: number;
    isLoading: boolean;
    fetchNotifications: () => Promise<void>;
    markAsRead: (id: string) => Promise<void>;
    markAllAsRead: () => Promise<void>;
}

const UserNotificationsContext = createContext<UserNotificationsContextType | undefined>(undefined);

export const UserNotificationsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const [notifications, setNotifications] = useState<UserNotification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const fetchNotifications = async () => {
        if (!user) return;
        setIsLoading(true);
        try {
            const res = await api.getNotifications();
            if (res.success) {
                setNotifications(res.notifications);
                const unread = res.notifications.filter((n: UserNotification) => !n.is_read || n.is_read === false).length;
                setUnreadCount(unread);
            }
        } catch (e) {
            console.error("Failed to fetch notifications", e);
        } finally {
            setIsLoading(false);
        }
    };

    const markAsRead = async (id: string) => {
        try {
            const res = await api.markNotificationRead(id);
            if (res.success) {
                setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (e) {
            console.error("Failed to mark notification as read", e);
        }
    };

    const markAllAsRead = async () => {
        try {
            const res = await api.markNotificationRead();
            if (res.success) {
                setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
                setUnreadCount(0);
            }
        } catch (e) {
            console.error("Failed to mark all notifications as read", e);
        }
    };

    // Poll for notifications every 30 seconds if user is logged in
    useEffect(() => {
        if (user) {
            fetchNotifications();
            const interval = setInterval(fetchNotifications, 30000);
            return () => clearInterval(interval);
        } else {
            setNotifications([]);
            setUnreadCount(0);
        }
    }, [user?.id]);

    // Handle incoming real-time notifications via custom events if needed
    useEffect(() => {
        const handleNewNotif = () => fetchNotifications();
        window.addEventListener('notification-refresh', handleNewNotif);
        return () => window.removeEventListener('notification-refresh', handleNewNotif);
    }, []);

    return (
        <UserNotificationsContext.Provider value={{
            notifications,
            unreadCount,
            isLoading,
            fetchNotifications,
            markAsRead,
            markAllAsRead
        }}>
            {children}
        </UserNotificationsContext.Provider>
    );
};

export const useUserNotifications = () => {
    const context = useContext(UserNotificationsContext);
    if (context === undefined) {
        throw new Error('useUserNotifications must be used within a UserNotificationsProvider');
    }
    return context;
};
